import { Form, Card, Badge, Button, Alert, Spinner } from 'react-bootstrap'
import { useState, useEffect } from 'react'
import axios from 'axios'
import { toast } from 'react-toastify'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  CHANNEL_CONFIG_MESSAGES as M,
  AI_PROVIDER_DIFY,
  EYE_HIDDEN,
  EYE_SHOWN,
  LLM_PROVIDER_OPTIONS,
  REQUIRED_MARK,
} from '../channelConfigMessages'

// Decoración del encabezado, no texto traducible.
const SECTION_ICON = '🤖'
const DEFAULT_DIFY_API_URL = 'https://api.dify.ai/v1'

type AiProvider = 'DIFY' | 'LLM'
type LlmProvider = 'ANTHROPIC' | 'OPENAI' | ''

interface DifySectionProps {
  difyEnabled: boolean
  difyApiKey: string
  difyApiUrl: string
  onDifyEnabledChange: (value: boolean) => void
  onDifyApiKeyChange: (value: string) => void
  onDifyApiUrlChange: (value: string) => void
  difyAppId: string
  onDifyAppIdChange: (value: string) => void
  organizationId: string
  switchLabel?: string
  // When set, the toggle is disabled and this message explains why
  // (e.g. no messaging channel configured yet).
  disabledReason?: string
  // Conversational AI: provider selector (Dify vs Direct LLM)
  aiProvider?: AiProvider
  onAiProviderChange?: (value: AiProvider) => void
  llmProvider?: LlmProvider
  onLlmProviderChange?: (value: LlmProvider) => void
  llmApiKey?: string
  onLlmApiKeyChange?: (value: string) => void
  llmModel?: string
  onLlmModelChange?: (value: string) => void
}

const LLM_MODEL_PLACEHOLDER: Record<Exclude<LlmProvider, ''>, string> = {
  ANTHROPIC: 'claude-sonnet-5',
  OPENAI: 'gpt-5',
}
const ANY_MODEL_EXAMPLE = 'claude-sonnet-5 / gpt-5'

// Términos de la API de Dify: iguales en EN y ES, así que son constantes y no
// llaves de traducción.
const API_KEY_LABEL = 'API Key'
const API_URL_LABEL = 'API URL'
const APP_ID_LABEL = 'App ID'

export const DifySection = ({
  difyEnabled,
  difyApiKey,
  difyApiUrl,
  onDifyEnabledChange,
  onDifyApiKeyChange,
  onDifyApiUrlChange,
  difyAppId,
  onDifyAppIdChange,
  organizationId,
  switchLabel,
  disabledReason,
  aiProvider = 'DIFY',
  onAiProviderChange,
  llmProvider = 'ANTHROPIC',
  onLlmProviderChange,
  llmApiKey = '',
  onLlmApiKeyChange,
  llmModel = '',
  onLlmModelChange,
}: DifySectionProps) => {
  const intl = useIntl()
  const [testingConnection, setTestingConnection] = useState(false)
  const [connectionStatus, setConnectionStatus] = useState<'success' | 'error' | null>(null)
  const [connectionMessage, setConnectionMessage] = useState('')
  const [showApiKey, setShowApiKey] = useState(false)

  const handleTestConnection = async () => {
    if (!difyApiKey || !difyApiUrl) {
      toast.warning(intl.formatMessage(M.aiFillKeyAndUrl))
      return
    }

    setTestingConnection(true)
    try {
      const response = await axios.get(`${difyApiUrl}/parameters`, {
        headers: { Authorization: `Bearer ${difyApiKey}` },
        timeout: 5000,
      })

      setConnectionStatus('success')
      setConnectionMessage(intl.formatMessage(M.aiConnectionOk))
      toast.success(intl.formatMessage(M.aiConnectedToast))
    } catch (error: any) {
      setConnectionStatus('error')
      const errorMsg =
        error.response?.status === 401
          ? intl.formatMessage(M.aiInvalidApiKey)
          : error.message || intl.formatMessage(M.aiGenericConnectionError)
      setConnectionMessage(intl.formatMessage(M.aiConnectionError, { message: errorMsg }))
      toast.error(intl.formatMessage(M.aiConnectFailedToast, { message: errorMsg }))
    } finally {
      setTestingConnection(false)
    }
  }

  useEffect(() => {
    if (!difyEnabled) {
      setConnectionStatus(null)
      setConnectionMessage('')
    }
  }, [difyEnabled])

  const llmShortName = LLM_PROVIDER_OPTIONS.find((o) => o.value === llmProvider)?.short
  const providerBadgeLabel = !difyEnabled
    ? intl.formatMessage(M.disabled)
    : aiProvider === 'LLM'
    ? llmShortName
      ? intl.formatMessage(M.aiBadgeDirectLlmWith, { model: llmShortName })
      : intl.formatMessage(M.aiDirectLlm)
    : AI_PROVIDER_DIFY

  return (
    <Card className="mb-4 border-0 shadow-sm">
      <Card.Header className="bg-light border-bottom">
        <div className="d-flex align-items-center justify-content-between">
          <h5 className="mb-0">
            <span className="me-2">{SECTION_ICON}</span>
            {intl.formatMessage(M.aiTitle)}
          </h5>
          <Badge bg={difyEnabled ? 'success' : 'secondary'}>
            {providerBadgeLabel}
          </Badge>
        </div>
      </Card.Header>
      <Card.Body>
        {disabledReason && (
          <Alert variant="warning" className="mb-3">
            <small>{intl.formatMessage(M.aiDisabledReason, {reason: disabledReason})}</small>
          </Alert>
        )}
        <Form.Group className="mb-3">
          <Form.Check
            type="switch"
            label={switchLabel || intl.formatMessage(M.aiEnableLabel)}
            checked={difyEnabled}
            onChange={(e) => onDifyEnabledChange(e.target.checked)}
            disabled={!!disabledReason}
            id="dify-enabled-switch"
          />
          <Form.Text className="text-muted d-block mt-2">
            {intl.formatMessage(M.aiEnableHint)}
          </Form.Text>
        </Form.Group>

        {difyEnabled && (
          <>
            {/* AI Provider selector */}
            <Form.Group className="mb-3">
              <Form.Label>{intl.formatMessage(M.aiProvider)}</Form.Label>
              <Form.Select
                value={aiProvider}
                onChange={(e) => onAiProviderChange?.(e.target.value as AiProvider)}
              >
                <option value="DIFY">{AI_PROVIDER_DIFY}</option>
                <option value="LLM">{intl.formatMessage(M.aiDirectLlm)}</option>
              </Form.Select>
              <Form.Text className="text-muted d-block mt-2">
                {intl.formatMessage(M.aiProviderHint)}
              </Form.Text>
            </Form.Group>

            {aiProvider === 'DIFY' && (
              <>
                {/* API Key */}
                <Form.Group className="mb-3">
                  <Form.Label>
                    {API_KEY_LABEL}
                    <span className="text-danger ms-1">{REQUIRED_MARK}</span>
                  </Form.Label>
                  <div className="input-group">
                    <Form.Control
                      type={showApiKey ? 'text' : 'password'}
                      value={difyApiKey}
                      onChange={(e) => onDifyApiKeyChange(e.target.value)}
                      placeholder="app-xxxxxxxxxxxxx"
                      className="form-control"
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={() => setShowApiKey(!showApiKey)}
                      title={intl.formatMessage(showApiKey ? M.aiHideKey : M.aiShowKey)}
                    >
                      {showApiKey ? EYE_SHOWN : EYE_HIDDEN}
                    </Button>
                  </div>
                  <Form.Text className="text-muted d-block mt-2">
                    <FormattedMessage
                      {...M.aiApiKeyHint}
                      values={{
                        a: (chunks) => (
                          <a href="https://cloud.dify.ai" target="_blank" rel="noopener noreferrer">
                            {chunks}
                          </a>
                        ),
                      }}
                    />
                  </Form.Text>
                </Form.Group>

                {/* API URL */}
                <Form.Group className="mb-3">
                  <Form.Label>{API_URL_LABEL}</Form.Label>
                  <Form.Control
                    type="url"
                    value={difyApiUrl}
                    onChange={(e) => onDifyApiUrlChange(e.target.value)}
                    placeholder={DEFAULT_DIFY_API_URL}
                  />
                  <Form.Text className="text-muted d-block mt-2">
                    {intl.formatMessage(M.aiApiUrlHint, { url: DEFAULT_DIFY_API_URL })}
                  </Form.Text>
                </Form.Group>

                {/* App ID */}
                <Form.Group className="mb-3">
                  <Form.Label>{APP_ID_LABEL}</Form.Label>
                  <Form.Control
                    type="text"
                    value={difyAppId}
                    onChange={(e) => onDifyAppIdChange(e.target.value)}
                    placeholder={intl.formatMessage(M.aiAppIdPlaceholder)}
                  />
                  <Form.Text className="text-muted d-block mt-2">
                    {intl.formatMessage(M.aiAppIdHint)}
                  </Form.Text>
                </Form.Group>

                {/* Test Connection Button */}
                <div className="mb-3">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={handleTestConnection}
                    disabled={testingConnection || !difyApiKey}
                    className="me-2"
                  >
                    {testingConnection ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        {intl.formatMessage(M.aiTesting)}
                      </>
                    ) : (
                      intl.formatMessage(M.aiTestConnection)
                    )}
                  </Button>
                  <Form.Text className="text-muted">
                    {intl.formatMessage(M.aiTestHint)}
                  </Form.Text>
                </div>

                {/* Connection Status Alert */}
                {connectionStatus && (
                  <Alert
                    variant={connectionStatus === 'success' ? 'success' : 'danger'}
                    className="mb-3"
                    dismissible
                    onClose={() => setConnectionStatus(null)}
                  >
                    <small>{connectionMessage}</small>
                  </Alert>
                )}
              </>
            )}

            {aiProvider === 'LLM' && (
              <>
                {/* LLM Provider */}
                <Form.Group className="mb-3">
                  <Form.Label>{intl.formatMessage(M.aiLlmProvider)}</Form.Label>
                  <Form.Select
                    value={llmProvider}
                    onChange={(e) => onLlmProviderChange?.(e.target.value as LlmProvider)}
                  >
                    {LLM_PROVIDER_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                {/* LLM API Key */}
                <Form.Group className="mb-3">
                  <Form.Label>
                    {API_KEY_LABEL}
                    <span className="text-danger ms-1">{REQUIRED_MARK}</span>
                  </Form.Label>
                  <div className="input-group">
                    <Form.Control
                      type={showApiKey ? 'text' : 'password'}
                      value={llmApiKey}
                      onChange={(e) => onLlmApiKeyChange?.(e.target.value)}
                      placeholder={llmProvider === 'OPENAI' ? 'sk-...' : 'sk-ant-...'}
                      className="form-control"
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={() => setShowApiKey(!showApiKey)}
                      title={intl.formatMessage(showApiKey ? M.aiHideKey : M.aiShowKey)}
                    >
                      {showApiKey ? EYE_SHOWN : EYE_HIDDEN}
                    </Button>
                  </div>
                </Form.Group>

                {/* Model */}
                <Form.Group className="mb-3">
                  <Form.Label>{intl.formatMessage(M.aiModel)}</Form.Label>
                  <Form.Control
                    type="text"
                    value={llmModel}
                    onChange={(e) => onLlmModelChange?.(e.target.value)}
                    placeholder={llmProvider ? LLM_MODEL_PLACEHOLDER[llmProvider] : ''}
                  />
                  <Form.Text className="text-muted d-block mt-2">
                    {intl.formatMessage(M.aiModelHint, {
                      model: llmProvider ? LLM_MODEL_PLACEHOLDER[llmProvider] : ANY_MODEL_EXAMPLE,
                    })}
                  </Form.Text>
                </Form.Group>
              </>
            )}

            {/* Info Box */}
            <div className="bg-light p-3 rounded border-start border-info border-4">
              <small className="text-muted d-block">
                <FormattedMessage {...M.aiNote} values={{ b: (chunks) => <strong>{chunks}</strong> }} />
              </small>
            </div>
          </>
        )}

        {!difyEnabled && (
          <Alert variant="info" className="mb-0">
            <small>{intl.formatMessage(M.aiDisabledNotice)}</small>
          </Alert>
        )}
      </Card.Body>
    </Card>
  )
}
